import { compact } from 'lodash';
import type { Recipe } from '../../type';
import type { RecipeSearchQuery } from '../../type/recipe';
import { deleteCall, get, post } from '../axios';

import type { DiaryWritableRes } from './dto/response/diary-writable.res.dto';
import type { InfluencerCreatorRes } from './dto/response/influencer-creator.res.dto';

/**
 * 레시피 API 클래스
 * 레시피 관련 API를 제공합니다.
 */
export class RecipeApi {
  /** 도메인 기본 경로 상수 */
  private static readonly BASE = '/recipe';

  static readonly queryKeys = {
    /** 집밥일기 작성 가능 여부 조회 QueryKey */
    getDiaryWritable: () => [`${RecipeApi.BASE}/diary/writable`],

    /** 단일 레시피 조회 QueryKey */
    getOneRecipe: (id: number | undefined) =>
      compact([`${RecipeApi.BASE}`, 'detail', id]),

    /** 내 북마크 목록 조회 QueryKey */
    getMyRecipeBookmark: () => [`${RecipeApi.BASE}`, 'me', 'bookmark'] as const,

    /** 레시피 검색 QueryKey */
    searchRecipes: (query?: Omit<RecipeSearchQuery, 'myDiaryOnly'>) =>
      compact([`${RecipeApi.BASE}`, 'search', query]),

    /** 최신 레시피 목록 QueryKey */
    latestRecipes: () => [`${RecipeApi.BASE}`, 'latest'] as const,

    /** 개인 맞춤 레시피 목록 QueryKey */
    personalRecipes: () => [`${RecipeApi.BASE}`, 'personal'] as const,

    /** 큐레이션 base key */
    curation: () => [`${RecipeApi.BASE}`, 'curation'] as const,

    /** 큐레이션 by name QueryKey */
    curationByName: (name: string) =>
      [`${RecipeApi.BASE}`, 'curation', name] as const,

    /** 밥풀루언서 목록 QueryKey */
    influencerCreators: () =>
      [`${RecipeApi.BASE}`, 'creator', 'influencer'] as const,

    /** 이 달의 추천 레시피 QueryKey */
    monthlyRecommendedRecipes: () =>
      [`${RecipeApi.BASE}`, 'monthly-recommended'] as const,
  };

  /**
   * 이 달의 추천 레시피 목록 조회 (최대 20개)
   */
  static async getMonthlyRecommendedRecipes(): Promise<Recipe[]> {
    const { data } = await get<Recipe[]>(
      `${RecipeApi.BASE}/monthly-recommended`,
    );
    return data;
  }

  /**
   * 선정된 밥풀루언서 목록 조회
   */
  static async getInfluencerCreators(): Promise<InfluencerCreatorRes[]> {
    const { data } = await get<InfluencerCreatorRes[]>(
      `${RecipeApi.BASE}/creator/influencer`,
    );
    return data;
  }

  /**
   * 집밥일기 작성 가능 여부 조회
   */
  static async getDiaryWritable(): Promise<DiaryWritableRes> {
    const { data } = await get<DiaryWritableRes>(
      `${RecipeApi.BASE}/diary/writable`,
    );
    return data;
  }

  /**
   * 레시피 북마크 추가
   */
  static async addBookmark(recipeId: number): Promise<void> {
    await post(`${RecipeApi.BASE}/${recipeId}/bookmark`, '');
  }

  /**
   * 레시피 북마크 제거
   */
  static async removeBookmark(recipeId: number): Promise<void> {
    await deleteCall(`${RecipeApi.BASE}/${recipeId}/bookmark`);
  }
}
